import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Document } from "@/api/generated";
import { DocumentTypeOptions } from "@/constants/management";
import { PropallyApiFactory } from "@/module/CustomApiFactory";

type Snapshot = { scope: string; documents: Document[]; counts: Map<number, number>; loading: boolean; error: boolean };
export const usePropertyDocuments = (propertyId: number | undefined, type?: number) => {
  const api = useMemo(() => PropallyApiFactory(), []);
  const scope = `${propertyId}:${type ?? "all"}`;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const requestRef = useRef(0);
  const mounted = useRef(true);
  const [snapshot, setSnapshot] = useState<Snapshot>({
    scope: "",
    documents: [],
    counts: new Map(),
    loading: true,
    error: false,
  });
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (scopeRef.current !== scope) return;
    const request = ++requestRef.current;
    if (propertyId == null || (type != null && (!Number.isInteger(type) || type < 0 || type > 11))) {
      setSnapshot({ scope, documents: [], counts: new Map(), loading: false, error: false });
      return;
    }
    setSnapshot((previous) => ({
      scope,
      documents: previous.scope === scope ? previous.documents : [],
      counts: previous.scope === scope ? previous.counts : new Map(),
      loading: true,
      error: false,
    }));
    const current = () => mounted.current && scopeRef.current === scope && requestRef.current === request;
    try {
      const types = type == null ? DocumentTypeOptions.map((option) => option.value) : [type];
      const results = await Promise.all(
        types.map(async (value) => {
          const { data } = await api.v1PropertiesPropertyIdDocumentsDocumentTypeFilesGet(propertyId, value, {
            withCredentials: true,
          });
          return { type: value, documents: data ?? [] };
        })
      );
      if (current())
        setSnapshot({
          scope,
          documents: results.reduce<Document[]>((all, result) => [...all, ...result.documents], []),
          counts: new Map(results.map((result) => [result.type, result.documents.length])),
          loading: false,
          error: false,
        });
    } catch (error) {
      console.error(error);
      if (current()) setSnapshot((previous) => ({ ...previous, loading: false, error: true }));
    }
  }, [api, propertyId, scope, type]);
  useEffect(() => {
    void refresh();
  }, [refresh]);

  const updateDocument = useCallback(
    (document: Document) => {
      if (scopeRef.current === scope) {
        ++requestRef.current;
        setSnapshot((previous) =>
          previous.scope === scope
            ? {
                ...previous,
                loading: false,
                error: false,
                documents: previous.documents.map((item) => (item.id === document.id ? document : item)),
              }
            : previous
        );
      }
    },
    [scope]
  );
  const removeDocument = useCallback(
    (id: number) => {
      if (scopeRef.current === scope) {
        ++requestRef.current;
        setSnapshot((previous) =>
          previous.scope === scope
            ? {
                ...previous,
                loading: false,
                error: false,
                documents: previous.documents.filter((item) => item.id !== id),
              }
            : previous
        );
      }
    },
    [scope]
  );
  const current = snapshot.scope === scope;
  return {
    documents: current ? snapshot.documents : [],
    counts: current ? snapshot.counts : new Map<number, number>(),
    loading: !current || snapshot.loading,
    error: current && snapshot.error,
    refresh,
    updateDocument,
    removeDocument,
    api,
    scope,
  };
};
